import { ORDER_STATES } from './orderState';
const CODE_RE = /^[A-Z]{3}$/;
const NUM_RE = /^\d+(\.\d+)?$/;
const HIDDEN_RE = /^\d{1,2}(\.\d{1,2})?$/;
const URL_RE = /^https?:\/\/\S+$/;
const SLUG_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
const SLUG_RULE = 'Латиница в нижнем регистре, цифры и _: card_rub';
/** Where a cashier is offered; fixed when it is created. */
export const CHANNELS = [
    ['web', 'Сайт'],
    ['bot', 'Telegram-бот'],
    ['ios', 'iOS'],
    ['android', 'Android'],
    ['shared', 'Общая'],
];
export const channelLabel = (type) => CHANNELS.find(([id]) => id === type)?.[1] ?? type;
/** «RUB, usd» → ['RUB', 'USD'], or why the API would refuse it. */
export function parseCurrencies(raw) {
    const list = raw
        .split(/[\s,;]+/)
        .map((c) => c.toUpperCase())
        .filter(Boolean);
    if (list.length === 0)
        return { list, error: 'Нужна хотя бы одна валюта' };
    const bad = list.find((c) => !CODE_RE.test(c));
    if (bad)
        return { list, error: `«${bad}» — не код валюты` };
    const dup = list.find((c, i) => list.indexOf(c) !== i);
    if (dup)
        return { list, error: `${dup} указана дважды` };
    return { list, error: null };
}
/** Empty → null (the service default); whole hours above 0 → number; else undefined. */
export function parseWindowHours(raw) {
    const t = raw.trim();
    if (t === '')
        return null;
    return /^\d+$/.test(t) && Number(t) > 0 ? Number(t) : undefined;
}
export function draftOf(sys) {
    return {
        name: sys.display_name ?? '',
        commission: sys.commission == null ? '' : String(sys.commission),
        currencies: sys.charge_currencies.join(', '),
        allCurrencies: sys.available_for_all_currencies,
        enabled: sys.enabled,
        reconcile: sys.reconcile_enabled,
        windowHours: sys.reconcile_window_hours == null ? '' : String(sys.reconcile_window_hours),
        hiddenCommission: sys.hidden_commission ? String(sys.hidden_commission) : '',
        returnSuccess: sys.return_url_success ?? '',
        returnFail: sys.return_url_fail ?? '',
        expireOrders: sys.expire_orders,
        visibleStates: [...sys.visible_order_states],
        acceptTest: sys.accept_test_purchases,
    };
}
const sameSet = (a, b) => a.length === b.length && a.every((item) => b.includes(item));
export function systemForm(sys, d) {
    const patch = {};
    const errors = {};
    const name = d.name.trim() || null;
    if (name !== sys.display_name)
        patch.display_name = name;
    const comm = d.commission.trim();
    if (comm !== '' && !NUM_RE.test(comm))
        errors.commission = 'Число, например 2.5';
    else if ((comm === '' ? null : Number(comm)) !== sys.commission) {
        patch.commission = comm === '' ? null : Number(comm);
    }
    const cur = parseCurrencies(d.currencies);
    if (cur.error)
        errors.currencies = cur.error;
    else if (cur.list.join() !== sys.charge_currencies.join())
        patch.charge_currencies = cur.list;
    if (d.allCurrencies !== sys.available_for_all_currencies) {
        patch.available_for_all_currencies = d.allCurrencies;
    }
    if (d.enabled !== sys.enabled)
        patch.enabled = d.enabled;
    const hours = parseWindowHours(d.windowHours);
    if (hours === undefined)
        errors.windowHours = 'Целое число часов больше 0';
    else if (hours !== sys.reconcile_window_hours)
        patch.reconcile_window_hours = hours;
    if (d.reconcile !== sys.reconcile_enabled)
        patch.reconcile_enabled = d.reconcile;
    // Empty is none: 0 %.
    const hidden = d.hiddenCommission.trim();
    if (hidden !== '' && !HIDDEN_RE.test(hidden))
        errors.hiddenCommission = 'От 0 до 99.99';
    else if ((hidden === '' ? 0 : Number(hidden)) !== sys.hidden_commission) {
        patch.hidden_commission = hidden === '' ? 0 : Number(hidden);
    }
    // Empty is the address set in the cashier's own account.
    const success = d.returnSuccess.trim();
    if (success && !URL_RE.test(success))
        errors.returnSuccess = 'Адрес начинается с https://';
    else if ((success || null) !== sys.return_url_success)
        patch.return_url_success = success || null;
    const fail = d.returnFail.trim();
    if (fail && !URL_RE.test(fail))
        errors.returnFail = 'Адрес начинается с https://';
    else if ((fail || null) !== sys.return_url_fail)
        patch.return_url_fail = fail || null;
    if (d.expireOrders !== sys.expire_orders)
        patch.expire_orders = d.expireOrders;
    // Known states in their usual order; any the panel doesn't know yet are kept.
    const visible = [
        ...ORDER_STATES.filter((state) => d.visibleStates.includes(state)),
        ...d.visibleStates.filter((state) => !ORDER_STATES.includes(state)),
    ];
    if (!sameSet(visible, sys.visible_order_states))
        patch.visible_order_states = visible;
    if (d.acceptTest !== sys.accept_test_purchases)
        patch.accept_test_purchases = d.acceptTest;
    return { patch, errors };
}
/** Why the cashier can't take payments yet, or null when it can. */
export function keysWarning(sys) {
    return sys.credentials_configured
        ? null
        : `Ключи ${sys.credentials_key} не настроены — оплаты не пройдут`;
}
export const emptyCreateDraft = () => ({
    code: '',
    provider: '',
    channel: 'web',
    name: '',
    currencies: '',
    credentialsKey: '',
});
/** A new cashier: the POST body, or what is wrong with the draft. The rest is set up after. */
export function createForm(d) {
    const errors = {};
    const code = d.code.trim();
    if (!code)
        errors.code = 'Нужен код';
    else if (!SLUG_RE.test(code) || code.length > 50)
        errors.code = SLUG_RULE;
    const provider = d.provider.trim();
    if (!provider)
        errors.provider = 'Нужен провайдер';
    const name = d.name.trim();
    if (name.length > 64)
        errors.name = 'Не длиннее 64 знаков';
    const cur = parseCurrencies(d.currencies);
    if (cur.error)
        errors.currencies = cur.error;
    const key = d.credentialsKey.trim();
    if (key && (!SLUG_RE.test(key) || key.length > 50))
        errors.credentialsKey = SLUG_RULE;
    const body = {
        code,
        provider,
        service_type: d.channel,
        charge_currencies: cur.list,
    };
    if (name)
        body.display_name = name;
    if (key)
        body.credentials_key = key;
    return { body, errors };
}
/** One line under the system's name in the list: channel, currencies, reach, reconciliation. */
export function systemSummary(sys) {
    return [
        channelLabel(sys.service_type),
        sys.charge_currencies.join(', '),
        sys.available_for_all_currencies ? 'для всех валют' : null,
        sys.reconcile_enabled ? `сверка ${sys.reconcile_window_hours ?? 24} ч` : 'без сверки',
        sys.commission != null ? `комиссия ${sys.commission}` : null,
    ]
        .filter(Boolean)
        .join(' · ');
}
